/**
 * UPLOAD A BLANK — `tasks/practice-documents.md` §1.4, §3.1, Phase 8.
 *
 * The first of the Documents tab's two dialogs. Its whole subject is a PDF that does not exist on
 * the server yet, and the one thing it must get right is that a file the server would refuse never
 * costs an upload.
 *
 * ── WHY THIS IS A SEPARATE DIALOG FROM THE SETTINGS ONE ──────────────────────────────────────
 *
 * `consent-form-settings` opens ONE dialog for add and edit because there both are the same POST
 * of the same object. Here they are not. This dialog's request is a multipart create whose
 * accepted body is a name, a category and a description; the settings dialog's is a JSON PUT of
 * ten delivery fields plus, separately, a re-inspecting `POST /:id/file`. Sharing one component
 * would mean a mode flag hiding or disabling most of the controls, and it would put the
 * `requiredInInvite` hard-block warning — a sentence about what happens to a patient who receives
 * this document — beside a file picker for a document that does not exist. Two dialogs, each with
 * one subject and one pair of endpoints, is the honest shape.
 *
 * ── WHY THE UPLOAD HAPPENS HERE RATHER THAN IN THE CALLER ────────────────────────────────────
 *
 * A refusal has to land back against the picker that produced it. §1c settled that the file error
 * is held on screen and never handed to a toast: it is the instruction for what to do next and it
 * has to survive being read twice. A dialog that closed and let the tab do the POST would have
 * nowhere to put the sentence — and nowhere to re-quote the cap the server just named either.
 */

import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FileObject } from '../../../../core/interfaces/file-object';
import { FeedbackService } from '../../../../core/services/feedback.service';
import {
  PracticeDocumentDto, PracticeDocumentService,
} from '../../../../core/services/practice-document.service';
import {
  INFORMATION_ONLY_CONSEQUENCE, INFORMATION_ONLY_SENTENCE, REQUIRES_RESPONSE_SENTENCE,
  matchingCategories,
} from '../practice-document-copy';
import { PracticeDocumentPdfChoice } from '../practice-document-pdf-choice';

/**
 * Which section's add icon was clicked (§14.2).
 *
 * The tab passes it; a caller that passes nothing gets `true`, the schema's own default and the
 * one that fails loudly rather than silently.
 */
export interface PracticeDocumentUploadDialogData {
  requiresResponse?: boolean;
  /** What the practice has already filed documents under. A SUGGESTION LIST, never a constraint. */
  categories?: string[];
}

/** What this dialog hands back. Nothing on a cancel, so `afterClosed` gets `undefined`. */
export interface PracticeDocumentUploadDialogResult {
  document: PracticeDocumentDto;
}

@Component({
  selector: 'app-practice-document-upload-dialog',
  templateUrl: './practice-document-upload-dialog.component.html',
  styleUrls: ['./practice-document-upload-dialog.component.scss'],
})
export class PracticeDocumentUploadDialogComponent extends BaseComponent {

  /** The picker, its four checks, its refusal and the caps it quotes. */
  readonly choice = new PracticeDocumentPdfChoice();

  name = '';
  category = '';

  /**
   * The categories this practice has already used, handed over by the tab.
   *
   * Passed rather than fetched because the tab is holding the unfiltered document list that they
   * come from — see its `documentCategories`. A dialog that went and asked would be a second
   * request for an answer already on screen behind it.
   */
  readonly categories: string[];

  /**
   * What to offer for what has been typed. A getter rather than a `valueChanges` stream: the list
   * is one practice's categories, the filter is a substring test, and a subscription here would be
   * more moving parts than the thing it computes.
   */
  get filteredCategories(): string[] {
    return matchingCategories(this.categories, this.category);
  }
  description = '';
  isUploading = false;

  /** §14.2 — set by the section the user clicked add in, and stated on screen. */
  readonly requiresResponse: boolean;

  constructor(
    private dialogRef: MatDialogRef<PracticeDocumentUploadDialogComponent,
      PracticeDocumentUploadDialogResult>,
    private practiceDocumentService: PracticeDocumentService,
    @Optional() @Inject(MAT_DIALOG_DATA) data: PracticeDocumentUploadDialogData | null,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
    this.requiresResponse = !data || data.requiresResponse !== false;
    this.categories = (data && data.categories) || [];
  }

  /**
   * §14.2's full consequence, drawn against an information-sheet upload.
   *
   * It moved here from the settings dialog with the toggle that used to reveal it. The kind is
   * decided at this screen and nowhere else now, so this is the only place the sentence can be
   * read before it is true of a document.
   */
  readonly informationOnlyConsequence = INFORMATION_ONLY_CONSEQUENCE;

  /** What the practice is about to create, in the register the rest of the feature uses. */
  get responseModeSentence(): string {
    return this.requiresResponse ? REQUIRES_RESPONSE_SENTENCE : INFORMATION_ONLY_SENTENCE;
  }

  /* ── Choosing the file ──────────────────────────────────────────────────────────────────── */

  /**
   * `app-file-upload` emits `FileObject[]`, NOT `File[]` — `{ url, type, name }` where `url` is a
   * `URL.createObjectURL` blob URL and the original `File` is gone.
   *
   * `PracticeDocumentPdfChoice.choose` needs a real `File` (`.name`, `.size`, `.arrayBuffer()`) and
   * `createDocument` posts one, so the file is reconstructed from the blob the way
   * `consent-form-settings.component.ts:408-411` already does it. The reconstructed file carries
   * the original NAME and BYTES, which is what keeps all four of `choose`'s checks working — in
   * particular `hasPdfHeader`, since the shared component's own `filterValidFiles` compares MIME
   * type only and would wave through the `.docx` renamed `.pdf` that check exists to catch.
   */
  async onFilesPicked(files: FileObject[]): Promise<void> {
    const picked = files && files.length ? files[0] : null;
    if (!picked) return;

    try {
      const blob = await (await fetch(picked.url)).blob();
      await this.selectFile(new File([blob], picked.name || 'document.pdf',
        { type: picked.type || 'application/pdf' }));
    } catch {
      this.choice.error = 'That file could not be read from this device. Try choosing it again.';
    } finally {
      // One blob URL per pick, and the dialog is reopened for every upload.
      URL.revokeObjectURL(picked.url);
    }
  }

  async selectFile(file: File): Promise<void> {
    await this.choice.choose(file);
    if (this.choice.file && !this.name.trim()) {
      this.name = PracticeDocumentPdfChoice.nameFromFileName(this.choice.file.name);
    }
  }

  clearFile(): void {
    this.choice.clear();
  }

  megabytes(bytes: number): string {
    return PracticeDocumentPdfChoice.megabytes(bytes);
  }

  /* ── The upload ─────────────────────────────────────────────────────────────────────────── */

  /**
   * The response carries the SERVER'S inspection, so the row handed back is taken from it rather
   * than assembled here — this client never decides a `fillMode`.
   *
   * Name, category, description and `requiresResponse` — nothing else. The DELIVERY FLAGS are
   * deliberately absent: they belong to the settings dialog, which the tab opens on the new row the
   * moment this one closes, so a document is never left half-configured. `requiresResponse` is here
   * rather than there because it is not a delivery route: it is what the document IS, the user has
   * already said which it is by which section they clicked add in, and it decides which section the
   * new row appears in.
   */
  upload(): void {
    if (this.isUploading) return;

    if (!this.choice.file) {
      this.choice.error = 'Choose a PDF to upload first.';
      return;
    }

    const name = this.name.trim();
    if (!name) {
      this.showToastMessage('Give the document a name first.');
      return;
    }

    this.isUploading = true;
    this.practiceDocumentService.createDocument(this.choice.file, {
      name,
      category: this.category.trim() || null,
      description: this.description.trim() || null,
      // §14.2 — the section the user added from. The delivery flags are still absent and still
      // belong to the settings dialog; this one is not a delivery route, it is what the document
      // IS, and it decides which section the new row lands in.
      requiresResponse: this.requiresResponse,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isUploading = false;
          this.showToastMessage(`"${response.data.name}" uploaded.`);
          this.dialogRef.close({ document: response.data });
        },
        error: (error) => {
          this.isUploading = false;
          // The cap the server named replaces the one this dialog was quoting, and the dialog
          // stays open so the refusal sits against the picker it is about.
          this.choice.applyServerCap(error);
          this.choice.error = PracticeDocumentPdfChoice.messageFor(
            error, 'The document could not be uploaded.');
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
